import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { OpisAccessModal } from "../components/OpisAccessModal";

export default function FoundingMembersPage() {
    const [open, setOpen] = useState(false);

    useSEO({
        title: "OPIS™ Founding Member Programme | Optimum Hospitality Solutions",
        description:
            "The OPIS™ Founding Member Programme. Founding Member applications are now open for a limited number of properties. Each application is reviewed personally by Sidney Adams.",
        path: "/opis/founding-members",
    });

    return (
        <div data-testid="founding-members-page">
            {/* Hero */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                        OPIS<sup className="text-[8px] ml-0.5">™</sup> Founding Member Programme
                    </span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy max-w-4xl">
                        Founding Member applications are now open.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">
                        The OPIS™ Founding Member Programme opens Executive Decision Intelligence to a limited number of hotel properties during the introductory phase. Each application is reviewed personally by Sidney Adams, Founder and Managing Director of Optimum Hospitality Solutions Ltd. Founding Member access is currently offered free of charge, subject to application and approval.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-4">
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            data-testid="fm-hero-apply"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover"
                        >
                            Apply for Founding Member Access <ArrowUpRight size={16} />
                        </button>
                        <Link
                            to="/opis"
                            data-testid="fm-hero-opis-link"
                            className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-navy text-brand-navy text-sm tracking-wide hover:bg-brand-navy hover:text-brand-stone"
                        >
                            About OPIS<sup className="text-[8px]">™</sup>
                        </Link>
                    </div>
                </div>
            </section>

            {/* What it is */}
            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">The programme</span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy">
                            A small cohort of properties, shaping OPIS™ from the inside.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5 text-brand-ink/80 leading-relaxed">
                        <p>
                            OPIS™ is being introduced through a founding member cohort of hotel properties across the United Kingdom. Founding members are the properties whose engagement, feedback and results shape the platform as it moves out of pilot.
                        </p>
                        <p>
                            Founding members receive the full Executive Business Health Review, the professionally reviewed executive report and the 90 day action plan, along with direct access to Sidney Adams during the pilot period. They also help set the direction of OPIS™ before it opens to a wider audience.
                        </p>
                        <p>
                            The programme is not a discount programme or a marketing programme. It is a small group of committed operators, chosen with care.
                        </p>
                    </div>
                </div>
            </section>

            {/* What founding members receive */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">What founding members receive</span>
                    <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy max-w-3xl">
                        The full OPIS™ experience, with a direct line to the operator behind it.
                    </h2>
                    <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 max-w-5xl">
                        {[
                            "The full Executive Business Health Review across six operational pillars",
                            "A professionally reviewed Executive Report, personally signed off by Sidney Adams",
                            "A tracked 90 day action plan with owners, priorities and due dates",
                            "A Business Health Timeline that grows with successive reviews",
                            "Direct access to Sidney Adams throughout the pilot period",
                            "A voice in the platform\u2019s roadmap during founding member cohort meetings",
                        ].map((line) => (
                            <li key={line} className="flex gap-3 text-brand-ink/80 leading-relaxed">
                                <Check className="text-brand-champagne shrink-0 mt-1" size={18} strokeWidth={1.5} />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Who we are looking for */}
            <section className="border-b border-brand-border bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Who we are looking for</span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-stone">
                            Serious operators. Considered ownership.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 space-y-5 text-brand-stone/80 leading-relaxed">
                        <p>
                            Founding members are hotel properties where the ownership and operating team are prepared to look honestly at operational health and act on the findings. Property type is broad. What matters is the intent.
                        </p>
                        <p>
                            Typical founding member properties include independent hotels, boutique groups, aparthotel operators and small multi site groups across the United Kingdom. We are not looking for a specific star rating or size.
                        </p>
                        <p>
                            We are looking for properties where the review will be treated as an executive process, not a compliance exercise, and where the outcome matters to more than one person in the building.
                        </p>
                    </div>
                </div>
            </section>

            {/* How the application works */}
            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">How the application works</span>
                    <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] tracking-tight text-brand-navy max-w-3xl">
                        Personally reviewed. Confidential. No obligation.
                    </h2>
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-px bg-brand-border max-w-5xl">
                        {[
                            { step: "01", title: "Submit application", body: "Share your name, company, email, country and a few details. Under two minutes." },
                            { step: "02", title: "Personal review", body: "Sidney Adams reviews every application personally, within one business day." },
                            { step: "03", title: "Discovery call", body: "If it looks like a fit, we hold a 30 minute confidential discovery call." },
                            { step: "04", title: "Founding Member onboarding", body: "If both parties are aligned, your OPIS™ member account is activated and onboarding begins." },
                        ].map((s) => (
                            <div key={s.step} className="bg-brand-stone p-8">
                                <div className="font-display text-3xl text-brand-champagne">{s.step}</div>
                                <h3 className="font-display text-lg text-brand-navy mt-4 leading-snug">{s.title}</h3>
                                <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 text-sm text-brand-ink/70 max-w-3xl leading-relaxed">
                        There is no obligation at any point in the process. If either party decides the fit is not right, the conversation ends there and your details are held only under our Privacy Policy.
                    </p>
                </div>
            </section>

            {/* Closing CTA */}
            <section className="bg-brand-stone">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-navy">
                            Ready to apply?
                        </h2>
                        <p className="mt-5 text-brand-ink/75 max-w-2xl text-lg">
                            Founding Member applications are now open. Share a few details and Sidney will review your application personally within one business day.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            data-testid="fm-bottom-apply"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover"
                        >
                            Apply for Founding Member Access <ArrowUpRight size={16} />
                        </button>
                    </div>
                </div>
            </section>

            <OpisAccessModal open={open} onOpenChange={setOpen} />
        </div>
    );
}
